/**
 * budgetModel — the ONE reading of "how much of the budget is gone".
 *
 * Three places answer this question: the Details economics mini card, the
 * Economics tab's KPI strip, and its consumption donut. They each derived it
 * themselves, from `matterData.fixed_fee_amount` — so a budget only existed
 * for fixed-fee and hybrid matters, and an hourly matter showed an em dash
 * with nothing it could ever show instead.
 *
 * The backend now returns `budget_amount` (falling back to the fixed fee when
 * unset) and `budget_remaining` on the economics rollup, so the derivation is
 * a read rather than a calculation. This module is where that read lives.
 *
 * THE TWO REMAINDERS ARE DIFFERENT NUMBERS.
 *
 *   `fixed_fee_remaining`  what is left of the agreed FEE
 *   `budget_remaining`     budget − (billed + WIP)
 *
 * For a fixed-fee matter revenue tracks the fee, so `budget_remaining` sits
 * near zero while `fixed_fee_remaining` is still the meaningful figure. They
 * are not interchangeable; the budget bar, donut and "Remaining" figure all
 * read `budget_remaining`.
 */

import type { MatterEconomicsSummary } from '../../../types/economics';

export interface BudgetReading {
  /** The denominator, or null when the matter has no budget at all. */
  budget: number | null;
  /** Invoiced. */
  billed: number;
  /** Recorded but not yet invoiced — `revenue − invoiced`, never negative. */
  wip: number;
  /** `billed + wip`. What the bar and donut fill with. */
  consumed: number;
  /**
   * The REAL remainder, negative on overrun. Read from the server when it
   * sends one, derived only as a fallback.
   */
  remaining: number | null;
  /** Consumed as a percentage of budget, null without a denominator. */
  usedPct: number | null;
  /** The same percentage clamped to 0–100 — for bar and ring WIDTHS only. */
  barPct: number;
  /** True once consumption passes the budget. */
  over: boolean;
  /** Whether there is a budget to measure against at all. */
  hasBudget: boolean;
}

const EMPTY: BudgetReading = {
  budget: null,
  billed: 0,
  wip: 0,
  consumed: 0,
  remaining: null,
  usedPct: null,
  barPct: 0,
  over: false,
  hasBudget: false,
};

const num = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * @param summary  the economics rollup, or null before it loads.
 * @param fallbackBudget  the matter's own `budget_amount` / `fixed_fee_amount`,
 *   used only when the rollup carries no budget — so the figure is right on
 *   the first paint after a save, before the summary has been refetched.
 */
export function readBudget(
  summary: MatterEconomicsSummary | null | undefined,
  fallbackBudget?: number | string | null
): BudgetReading {
  if (!summary) return EMPTY;

  const billed = num(summary.invoiced_amount);
  const wip = Math.max(0, num(summary.revenue) - billed);
  const consumed = billed + wip;

  const rawBudget = summary.budget_amount ?? fallbackBudget;
  const budget =
    rawBudget == null || rawBudget === '' || !Number.isFinite(Number(rawBudget))
      ? null
      : Number(rawBudget);

  // A budget of zero is not a budget to measure against — it would make every
  // percentage infinite. Treated as "none", same as null.
  const hasBudget = budget != null && budget > 0;

  if (!hasBudget) {
    return { ...EMPTY, billed, wip, consumed, budget };
  }

  const denominator = budget as number;
  // The server's own remainder wins; the subtraction is only for a payload
  // that predates the field.
  const remaining =
    summary.budget_remaining != null
      ? Number(summary.budget_remaining)
      : denominator - consumed;

  const usedPct = Math.round((consumed / denominator) * 100);

  return {
    budget: denominator,
    billed,
    wip,
    consumed,
    remaining,
    usedPct,
    // Clamped, because a 140% bar would render past its track. The unclamped
    // number is still in `usedPct` and the real remainder in `remaining`.
    barPct: Math.min(100, Math.max(0, usedPct)),
    over: consumed > denominator,
    hasBudget: true,
  };
}

/** Segment widths for a two-tone consumption bar, clamped to the track. */
export function budgetBarWidths(reading: BudgetReading): { billed: number; wip: number } {
  if (!reading.hasBudget || !reading.budget) return { billed: 0, wip: 0 };
  const billed = Math.min(100, (reading.billed / reading.budget) * 100);
  // The WIP segment gets whatever room the billed segment left, so the two
  // together never overflow the track on an overrun.
  const wip = Math.min(100 - billed, (reading.wip / reading.budget) * 100);
  return { billed, wip: Math.max(0, wip) };
}

export default readBudget;
